import { useCallback } from "react";
import { useAsync } from "@shared/hooks";
import { legacyHttpClient } from "@services/http/legacyHttpClient";

export interface PaymentMethodCms {
  id: number;
  code: string;
  legacyCode: number;
  name: string;
  type: string;
  logo?: string;
}

/** Local logo map by payment code — used as fallback when API doesn't return logo. */
const LOGO_BY_CODE: Record<string, string> = {
  PAYPHONE: "/images/payphone.avif",
  DEUNA: "/images/deuna.png",
  JEP: "/images/jep.png",
  COOPERCO: "/images/cooperco.png",
  PLUX: "/images/plux.png",
};

/** Override local del nombre visible por código (independiente del que envíe el CMS). */
const NAME_BY_CODE: Record<string, string> = {
  PLUX: "Plux (Tarjeta de crédito / débito)",
};

/** Fallback when the CMS endpoint is unavailable or returns empty. */
const DEFAULT_PAYMENT_METHODS: PaymentMethodCms[] = [
  { id: 1, code: "PAYPHONE", legacyCode: 1, name: "Tarjeta de crédito / débito", type: "card", logo: LOGO_BY_CODE.PAYPHONE },
  { id: 2, code: "DEUNA", legacyCode: 2, name: "De Una (billetera digital)", type: "wallet", logo: LOGO_BY_CODE.DEUNA },
  { id: 3, code: "JEP", legacyCode: 3, name: "JEP (cooperativa)", type: "wallet", logo: LOGO_BY_CODE.JEP },
  { id: 4, code: "COOPERCO", legacyCode: 7, name: "Cooperco", type: "qr", logo: LOGO_BY_CODE.COOPERCO },
  { id: 5, code: "PLUX", legacyCode: 8, name: "Plux (Tarjeta de crédito / débito)", type: "card", logo: LOGO_BY_CODE.PLUX },
];

/** Enriquecer un método con el logo local y el nombre override (si aplica) por código. */
function enrichMethod(method: PaymentMethodCms): PaymentMethodCms {
  return {
    ...method,
    name: NAME_BY_CODE[method.code] ?? method.name,
    logo: method.logo || LOGO_BY_CODE[method.code],
  };
}

/**
 * Hook to fetch the list of active payment methods from the CMS API.
 * GET /rest/api/web/cms/payment-methods
 * Falls back to DEFAULT_PAYMENT_METHODS when CMS returns an empty array.
 * Always enriches methods with local logos when the API field is absent.
 */
export function usePaymentMethods() {
  const fetcher = useCallback(async () => {
    try {
      const methods = await legacyHttpClient.get<PaymentMethodCms[]>(
        "/api/web/cms/payment-methods",
      );
      if (methods.length === 0) return DEFAULT_PAYMENT_METHODS.map(enrichMethod);
      return methods.map(enrichMethod);
    } catch {
      return DEFAULT_PAYMENT_METHODS.map(enrichMethod);
    }
  }, []);

  return useAsync<PaymentMethodCms[]>(fetcher);
}
